import { useOffline } from "../../../offline/OfflineProvider";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "./Chat.css";
import Logo from "images/allrecobot-logo.webp";
import TrashIcon from "./icons/TrashIcon";
import DownloadIcon from "./icons/DownloadIcon";
import CloseIcon from "./icons/CloseIcon";
import ArrowDownIcon from "./icons/ArrowDownIcon";
import { capture } from "../../../utils/analytics";
import { apiGet } from "../../../utils/apiClient";
import { readSealed, writeSealed, removeSealed } from "../../../offline/sealedLocal.js";
import { streamAgentAnswer, phaseIcon, formatDuration } from "./agentStream";
import FoldedMarkdown from "./FoldedMarkdown";
import { shouldShowShort, detailsLabel, fullAnswerText } from "./shortAnswerView";
import { citationTitle } from "./citationPreview";

// Display-only prettification of source document names (headings, citation
// labels, tooltips). Never used for citation matching — normalize()/lookup
// keep operating on the raw displayName.
const prettifyDocName = (name) =>
  (name || "")
    .replace(/\.(pdf|docx?|xlsx?|xml|dwg|stp|step)$/i, "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const OVERLAY_KEY = "allreco-overlay-accepted-at";
const OVERLAY_VALID = 12 * 60 * 60 * 1000; // 12h in ms
const CHAT_WIDTH_KEY = "chat-width-preference";
const CHAT_SESSION_KEY = "allreco-chat-session-id";
const DEFAULT_CHAT_WIDTH = 600;
const MIN_CHAT_WIDTH = 300;
const MAX_CHAT_WIDTH = 1000;

// CommonMark (react-markdown + remark-gfm) only lets an ordered list
// interrupt a paragraph when it starts at `1.`. The answer LLM groups
// enumerations under bold subheads and numbers them CONTINUOUSLY across
// groups, so everything after the first group looks like this:
//
//   **BHS Rotorshredder**
//   4. Motorabsicherungen [S.19]
//   5. Motorschutzschalter [S.19]
//
// The `4.` cannot start a list (it isn't `1.`) and there is no blank line,
// so it is swallowed as a lazy continuation of the `**BHS Rotorshredder**`
// paragraph and the whole group renders as one run-on <p>. Measured on the
// prod "Schutzeinrichtungen" answer: only 3 of 46 items were real <li>
// (2026-07-24 chip audit) — group 1 started at `1.` and became an <ol>,
// groups 2-7 all collapsed.
//
// Inserting the blank line the parser needs makes each group its own list.
// A list already separated by a blank line is untouched, and bullet lists
// (`-`/`*`/`+`) never had the problem — they may interrupt a paragraph.
const ORDERED_ITEM_RX = /^\s*\d+[.)]\s/;
const LIST_ITEM_RX = /^\s*(?:[-*+]\s|\d+[.)]\s)/;

const normalizeMarkdownLists = (md) => {
  if (typeof md !== 'string' || md.indexOf('\n') === -1) return md;
  const lines = md.split('\n');
  const out = [];
  let inFence = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // Never rewrite inside fenced code — a "1. " there is literal text.
    if (/^\s*(?:```|~~~)/.test(line)) inFence = !inFence;
    const prev = i > 0 ? lines[i - 1] : '';
    if (
      !inFence &&
      ORDERED_ITEM_RX.test(line) &&
      prev.trim() !== '' &&
      !LIST_ITEM_RX.test(prev)
    ) {
      out.push('');
    }
    out.push(line);
  }
  return out.join('\n');
};

const Chat = ({ setDocumentViewerState, onChatToggle }) => {
  const { isOnline } = useOffline();
  /* ---------- refs ---------- */
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const chatContentRef = useRef(null);
  const chatContainerRef = useRef(null);
  const isResizingRef = useRef(false);
  // Per-message-section cache of react-markdown `components` objects.
  // createMarkdownComponents() rebuilds fresh function identities on every
  // call; passing a new `components` object to <ReactMarkdown> makes React
  // treat every p/li/h*/a/code as a new element TYPE and remount the whole
  // message subtree inside the scroll container — which loses the chat
  // scroll position. Caching by (message-section key + source/citation
  // signature) returns a STABLE object across re-renders so the markdown
  // reconciles in place instead of remounting (no scroll jump on citation
  // click, no per-token remount during streaming, no remount on resize).
  const mdComponentsCacheRef = useRef(new Map());
  // Session ID for conversation memory - loaded/generated once
  const sessionIdRef = useRef(null);
  if (sessionIdRef.current === null) {
    try {
      const saved = localStorage.getItem(CHAT_SESSION_KEY);
      if (saved) {
        sessionIdRef.current = saved;
      }
    } catch { /* ignore */ }
    if (!sessionIdRef.current) {
      // Generate new session ID: timestamp + random suffix
      sessionIdRef.current = `chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      try {
        localStorage.setItem(CHAT_SESSION_KEY, sessionIdRef.current);
      } catch { /* ignore */ }
    }
  }

  /* ---------- state ---------- */
  // Muss mit App.jsx übereinstimmen: auf ≤768px (Vollbild-Overlay) geschlossen starten
  const [isChatOpen, setIsChatOpen] = useState(() => window.innerWidth > 768);
  const [messages, setMessages] = useState(() => {
    // Load messages saved earlier (preloaded at boot, encrypted when OFFLINE_ENCRYPTION is on)
    const saved = readSealed("chat", []);
    return Array.isArray(saved) ? saved : [];
  });
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [messageHistory, setMessageHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [chatWidth, setChatWidth] = useState(DEFAULT_CHAT_WIDTH);
  const [expandedThinking, setExpandedThinking] = useState({});
  const [stickyMode, setStickyMode] = useState(false);
  const [isAwayFromBottom, setIsAwayFromBottom] = useState(false);
  const navigate = useNavigate();

  /* ---------- sync chat width to CSS variable for layout ---------- */
  useEffect(() => {
    const root = document.documentElement;
    if (isChatOpen) {
      root.style.setProperty('--chat-width', `${chatWidth}px`);
    } else {
      root.style.removeProperty('--chat-width');
    }
  }, [isChatOpen, chatWidth]);

  /* ---------- scroll on user action only ----------
     Incoming/streaming content never moves the scroll position. The chat
     jumps to the bottom only when the user sends/regenerates a message
     and when the panel (re)opens. behavior:"instant" bypasses the CSS
     scroll-behavior:smooth on .chat-content ("auto" would defer to it);
     rAF waits for React to commit the new message first. */
  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
    });
  }, []);

  useEffect(() => {
    if (isChatOpen) scrollToBottom();
  }, [isChatOpen, scrollToBottom]);

  /* ---------- opt-in sticky-follow ----------
     The floating ↓ button arms follow mode: every messages mutation
     scrolls to the bottom until the user scrolls (wheel-up, touch drag,
     or scrollbar/keyboard leaving the bottom zone) — then it disarms.
     Only the button re-arms it; reaching the bottom by hand does not. */
  const measureAwayFromBottom = useCallback(() => {
    const el = chatContentRef.current;
    if (!el) return;
    setIsAwayFromBottom(el.scrollHeight - el.scrollTop - el.clientHeight > 150);
  }, []);

  useEffect(() => {
    if (stickyMode) scrollToBottom();
    // measure after the (possible) follow scroll in the same frame batch
    requestAnimationFrame(measureAwayFromBottom);
  }, [messages, stickyMode, scrollToBottom, measureAwayFromBottom]);

  useEffect(() => {
    const el = chatContentRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (e.deltaY < 0) setStickyMode(false);
    };
    const onTouchMove = () => setStickyMode(false);
    const onScroll = () => {
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
      // Programmatic follow scrolls land at distance ≈ 0 and never disarm.
      if (distance > 80) setStickyMode(false);
      setIsAwayFromBottom(distance > 150);
    };
    el.addEventListener("wheel", onWheel, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("scroll", onScroll);
    };
  }, [isChatOpen]);

  /* ---------- focus input when chat opens ---------- */
  useEffect(() => {
    if (isChatOpen && !showOverlay) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isChatOpen, showOverlay]);

  /* ---------- save messages to localStorage ---------- */
  useEffect(() => {
    if (messages.length > 0) {
      writeSealed("chat", messages);
    }
  }, [messages]);

  /* ---------- welcome messages with suggestions ---------- */
  // Topic chips (2026-09-23). Questions about ONE component answer well
  // (150–370 words); "all faults / all intervals of all machines" came back
  // as 675–1,348-word walls, and a bare prompt chip cannot know the tenant's
  // machines. So a chip names a topic, the bot answers locally with the
  // tenant's components as a second row of chips (no LLM call), and only the
  // component pick sends a question — or, for a fault, prefills the input so
  // the user adds the symptom. Real chat traffic is fault > maintenance >
  // spare parts > technical data, hence these four.
  // Prompts must still read as CONTENT questions: inventory/meta framing
  // ("liste/welche Dokumente", verbs like "nenne/erwähne") classifies as
  // shape=browse, whose short-circuit skips the answer LLM (2026-07-24 audit).
  const CHAT_TOPICS = useMemo(() => ({
    fault: {
      ask: "Gern. An welcher Komponente tritt die Störung auf? Danach beschreiben Sie kurz das Fehlerbild. Einen Fehlercode aus der Anzeige können Sie auch direkt eingeben, z. B. „Was bedeutet 1-PN01?“.",
      prefill: (name) => `Störung an ${name}: `,
    },
    maintenance: {
      ask: "Gern. Für welche Komponente?",
      prompt: (name) => `Welche Wartungsarbeiten fallen bei ${name} an und in welchen Intervallen?`,
    },
    spareParts: {
      ask: "Gern. Für welche Komponente?",
      prompt: (name) => `Ersatzteilliste ${name}`,
    },
    specs: {
      ask: "Gern. Für welche Komponente?",
      prompt: (name) => `Technische Daten ${name}`,
    },
  }), []);

  const welcomeSuggestions = useMemo(() => [
    { label: "Störung beheben",  topic: "fault" },
    { label: "Wartung",          topic: "maintenance" },
    { label: "Ersatzteile",      topic: "spareParts" },
    { label: "Technische Daten", topic: "specs" }
  ], []);

  // The tenant's pickable components: leaf nodes of the parts hierarchy that
  // hold at least one document (inner nodes like "Zulieferer" group vendors),
  // merged by name, alphabetical. Loaded on the first topic click and cached.
  const componentsRef = useRef(null);
  const loadComponents = async () => {
    if (componentsRef.current) return componentsRef.current;
    try {
      const [parts, counts] = await Promise.all([
        apiGet("/api/parts"),
        apiGet("/api/parts/document-counts"),
      ]);
      const parents = new Set((parts || []).map((p) => String(p.parent_id || "")));
      const byName = new Map();
      for (const p of parts || []) {
        const name = (p.name || "").trim();
        if (!name || parents.has(String(p.id))) continue;
        const n = Number((counts || {})[String(p.id)] || 0);
        if (n > 0) byName.set(name, (byName.get(name) || 0) + n);
      }
      const names = [...byName.keys()].sort((x, y) => x.localeCompare(y, "de"));
      if (names.length) componentsRef.current = names;
      return names;
    } catch {
      return [];
    }
  };

  useEffect(() => {
    // Only show welcome message if no messages loaded from localStorage
    if (messages.length === 0) {
      setTimeout(() => {
        const initialMessage = {
          id: Date.now(),
          text: "👋 Hallo! Ich bin Ihr KI-Assistent für Dokumentation. Wie kann ich Ihnen heute helfen?",
          from: "bot",
          timestamp: new Date(),
          reactions: [],
          suggestions: welcomeSuggestions,
          isWelcome: true
        };
        setMessages([initialMessage]);
      }, 300);
    }
  }, [welcomeSuggestions]);

  /* ---------- overlay visibility ---------- */
  useEffect(() => {
    try {
      const saved = localStorage.getItem(OVERLAY_KEY);
      if (saved && Date.now() - Number(saved) < OVERLAY_VALID) {
        setShowOverlay(false);
        return;
      }
    } catch {
      /* ignore */
    }
    setShowOverlay(true);
  }, []);

  /* ---------- load saved chat width ---------- */
  useEffect(() => {
    try {
      const savedWidth = localStorage.getItem(CHAT_WIDTH_KEY);
      if (savedWidth) {
        const width = parseInt(savedWidth, 10);
        if (width >= MIN_CHAT_WIDTH && width <= MAX_CHAT_WIDTH) {
          setChatWidth(width);
        }
      }
    } catch {
      /* ignore */
    }
  }, []);

  const acceptOverlay = () => {
    try {
      localStorage.setItem(OVERLAY_KEY, Date.now().toString());
    } catch {
      /* ignore */
    }
    setShowOverlay(false);
  };

  /* ---------- resize handlers ---------- */
  const startResize = useCallback((e) => {
    isResizingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    e.preventDefault();
  }, []);

  const resize = useCallback((e) => {
    if (!isResizingRef.current) return;

    const newWidth = window.innerWidth - e.clientX;
    const clampedWidth = Math.max(MIN_CHAT_WIDTH, Math.min(MAX_CHAT_WIDTH, newWidth));

    setChatWidth(clampedWidth);
  }, []);

  const stopResize = useCallback(() => {
    if (isResizingRef.current) {
      isResizingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';

      // Save to localStorage
      try {
        localStorage.setItem(CHAT_WIDTH_KEY, chatWidth.toString());
      } catch {
        /* ignore */
      }
    }
  }, [chatWidth]);

  useEffect(() => {
    document.addEventListener('mousemove', resize);
    document.addEventListener('mouseup', stopResize);

    return () => {
      document.removeEventListener('mousemove', resize);
      document.removeEventListener('mouseup', stopResize);
    };
  }, [resize, stopResize]);

  /* ---------- handlers ---------- */
  const toggleChat = () => {
    const nextOpen = !isChatOpen;
    setIsChatOpen(nextOpen);
    onChatToggle?.(nextOpen);
    if (nextOpen) {
      setShowHistory(false);
    }
  };

  const handleSuggestionClick = async (suggestion) => {
    // suggestion is a string (legacy), {label, prompt}, {label, navigateTo},
    // {label, reply}, {label, topic} or {label, pick: {topic, name}}
    if (suggestion && suggestion.navigateTo) {
      navigate(suggestion.navigateTo);
      return;
    }
    if (suggestion && suggestion.topic && CHAT_TOPICS[suggestion.topic]) {
      // Topic chip, answered locally — no agent call.
      const topic = CHAT_TOPICS[suggestion.topic];
      const names = await loadComponents();
      const now = Date.now();
      setMessages((prev) => [
        ...prev,
        { id: now, text: suggestion.label, from: "user", timestamp: new Date(), reactions: [], status: "sent" },
        {
          id: now + 1,
          text: names.length ? topic.ask : `${topic.ask} Nennen Sie die Maschine oder Anlage einfach in Ihrer Frage.`,
          from: "bot",
          timestamp: new Date(),
          reactions: [],
          isCanned: true,
          suggestions: names.map((name) => ({ label: name, pick: { topic: suggestion.topic, name } })),
        },
      ]);
      capture("suggestion_topic_shown", { topic: suggestion.topic, components: names.length, session_id: sessionIdRef.current });
      setTimeout(() => inputRef.current?.focus(), 100);
      return;
    }
    if (suggestion && suggestion.pick && CHAT_TOPICS[suggestion.pick.topic]) {
      const topic = CHAT_TOPICS[suggestion.pick.topic];
      capture("suggestion_component_picked", { topic: suggestion.pick.topic, component: suggestion.pick.name, session_id: sessionIdRef.current });
      if (topic.prefill) {
        setInput(topic.prefill(suggestion.pick.name));
        setTimeout(() => {
          const el = inputRef.current;
          if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); }
        }, 50);
        return;
      }
      const text = topic.prompt(suggestion.pick.name);
      setInput(text);
      sendMessageWithText(text);
      return;
    }
    if (suggestion && suggestion.reply) {
      // Canned follow-up question, answered locally — no agent call.
      const now = Date.now();
      setMessages((prev) => [
        ...prev,
        { id: now, text: suggestion.label, from: "user", timestamp: new Date(), reactions: [], status: "sent" },
        { id: now + 1, text: suggestion.reply, from: "bot", timestamp: new Date(), reactions: [], isCanned: true },
      ]);
      capture("suggestion_reply_shown", { label: suggestion.label, session_id: sessionIdRef.current });
      setTimeout(() => inputRef.current?.focus(), 100);
      return;
    }
    const text = typeof suggestion === "string" ? suggestion : suggestion.prompt;
    setInput(text);
    sendMessageWithText(text);
  };

  const clearChat = () => {
    const initialMessage = {
      id: Date.now(),
      text: "👋 Hallo! Ich bin Ihr KI-Assistent für Dokumentation. Wie kann ich Ihnen heute helfen?",
      from: "bot",
      timestamp: new Date(),
      reactions: [],
      suggestions: welcomeSuggestions,
      isWelcome: true
    };
    setMessages([initialMessage]);
    setMessageHistory([]);
    // Clear localStorage and reset session
    try {
      removeSealed("chat");
      // Generate new session ID for fresh conversation
      const newSessionId = `chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem(CHAT_SESSION_KEY, newSessionId);
      sessionIdRef.current = newSessionId;
    } catch {
      /* ignore localStorage errors */
    }
  };

  const copyMessage = (text, messageId) => {
    navigator.clipboard.writeText(text.replace(/<[^>]*>/g, ''));
    setCopiedMessageId(messageId);
    setTimeout(() => setCopiedMessageId(null), 2000);
  };

  const regenerateResponse = async (userMessage) => {
    if (!userMessage || isLoading) return;

    capture("answer_regenerated", { query: userMessage, session_id: sessionIdRef.current });

    // Remove the last bot response (user message is already there)
    setMessages(prev => prev.slice(0, -1));
    setIsLoading(true);
    scrollToBottom();

    try {
      await streamAgentAnswer({
        term: userMessage,
        sessionId: sessionIdRef.current,
        setMessages,
        context: "regenerate",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const exportChat = () => {
    const chatText = messages.map(msg => 
      `${msg.from === 'user' ? 'User' : 'Bot'}: ${msg.text.replace(/<[^>]*>/g, '')}`
    ).join('\n\n');
    
    const blob = new Blob([chatText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-export-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Core message sending logic - accepts text directly to avoid React state timing issues
  const sendMessageWithText = async (messageText) => {
    if (!messageText?.trim() || isLoading) return;

    const userMessageText = messageText.trim();
    setIsLoading(true);
    capture("query_submitted", { query: userMessageText, session_id: sessionIdRef.current });

    /* ---------- echo user ---------- */
    const userMessage = {
      id: Date.now(),
      text: userMessageText,
      from: "user",
      timestamp: new Date(),
      reactions: [],
      status: "sent"
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput(""); // Clear input immediately
    scrollToBottom();

    try {
      await streamAgentAnswer({
        term: userMessageText,
        sessionId: sessionIdRef.current,
        setMessages,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Wrapper for input field - reads from state
  const sendMessage = () => {
    sendMessageWithText(input);
  };

  // Step presentation (icon, duration) comes from ./agentStream, which
  // mirrors the backend's phase vocabulary in rag_agent/react/labels.py.
  //
  // What used to sit here was `makeStepFriendly` — a regex translator
  // written for the agent's old ENGLISH step strings ("Found 12
  // documents, selected 3"). The backend has emitted German for a long
  // time, so the translator only mislabelled: it tested for "document"
  // BEFORE "section", so "Lade Inhaltsverzeichnis" (list_document_
  // sections) was rewritten to "Durchsuche Dokumente...". Steps are now
  // rendered verbatim — the backend owns the wording.

  // Default-open during streaming, default-closed after — but explicit
  // user toggle always wins. `expandedThinking[id]` undefined = use default,
  // boolean = user override.
  const isThinkingExpanded = (message) => {
    const pref = expandedThinking[message.id];
    if (pref !== undefined) return pref;
    return message.status === "thinking" || message.status === "generating_answer";
  };

  // Toggle thinking trace expansion — flip relative to the currently-visible
  // state, not relative to `prev[id]` (which is undefined on first click and
  // would always resolve to `true`, ignoring the user's intent to collapse a
  // panel that's open by default during streaming).
  const toggleThinkingTrace = (message) => {
    const currentlyOpen = isThinkingExpanded(message);
    setExpandedThinking(prev => ({
      ...prev,
      [message.id]: !currentlyOpen
    }));
  };

  // Helper function to process markdown text and create clickable document references
  const createMarkdownComponents = (sources, citations, sectionDocId = null) => {
    // Normalize for matching: remove .pdf extension, lowercase, trim
    const normalize = (str) => (str || '').replace(/\.(pdf|xlsx|docx|xml|dwg|stp)$/i, '').trim().toLowerCase();

    // Look up source by docId; fall back to displayName match.
    const findSourceByDocId = (docId) => {
      if (!docId) return null;
      const n = normalize(docId);
      return (sources || []).find(s =>
        normalize(s.docId) === n || normalize(s.displayName) === n || normalize(s.displayName).includes(n)
      ) || null;
    };

    // Build a clickable citation span from a structured citation object.
    // Used for the new ⟦N⟧ marker path.
    // `localContext` is the LLM's prose immediately preceding the marker
    // (within the same bullet/line). It's the best locator for xlsx
    // citations where many markers point to the same chunk — each marker
    // should highlight *its own* row, not the chunk's aggregate row.
    const renderStructuredCitation = (cit, key, localContext = '') => {
      const source = findSourceByDocId(cit.docId);
      if (!source || !source.path) {
        // No source in payload — render as plain marker text so user sees something
        return `[S.${cit.page}]`;
      }
      const ext = source.path.split('.').pop().toLowerCase();
      const inlineTypes = { pdf: 'pdf', docx: 'docx', xlsx: 'xlsx', csv: 'csv', xml: 'xml', stp: 'stp', dwg: 'dwg' };
      if (!inlineTypes[ext]) {
        return `[${prettifyDocName(source.displayName) || 'Dokument'}, S.${cit.page}]`;
      }
      const isPageRef = cit.kind === 'pageref';
      // Cross-doc indicator (F5, chrome-mcp audit 2026-05-21): when this
      // citation points to a different doc than its parent section's
      // heading, badge gets a visual marker + a tooltip telling the user
      // which doc will open. Post-regroup these are bullets with multi-
      // citations like `⟦1⟧ ⟦5⟧` where cit 5 points elsewhere; the bullet
      // can't be split server-side without duplicating prose.
      const isCrossDoc = !!(sectionDocId && cit.docId && sectionDocId !== cit.docId);
      const className = [
        'document-reference-link',
        isPageRef ? 'document-reference-pageref' : '',
        isCrossDoc ? 'document-reference-cross-doc' : '',
      ].filter(Boolean).join(' ');
      const prettyName = prettifyDocName(source.displayName) || 'Dokument';
      // Hover shows the cited source text (citationPreview.js) next to the open hint.
      const title = citationTitle(prettyName, cit, { isCrossDoc, isPageRef });
      return (
        <span
          key={key}
          className={className}
          role="button"
          tabIndex={0}
          title={title}
          aria-label={title}
          onClick={() => {
            capture("citation_clicked", {
              docName: source.displayName,
              page: cit.page,
              ext,
              kind: "structured",
            });
            const viewerState = {
              type: inlineTypes[ext],
              url: source.path,
              displayName: source.displayName,
            };
            if (ext === 'pdf') {
              viewerState.page = cit.page;
              if (isPageRef) {
                // Page jump only — no highlight
                viewerState.highlightBboxes = [];
              } else {
                // Content citation — find the exact block and use its bbox.
                let precisionBbox = null;
                const chunksForPage = (source.chunks || []).filter(c =>
                  Number(c.page) === cit.page ||
                  (c.bboxes || []).some(b => Number(b.page) === cit.page) ||
                  (c.blocks || []).some(b => Number(b.page) === cit.page)
                );
                if (cit.blockId != null) {
                  for (const ch of chunksForPage) {
                    const blk = (ch.blocks || []).find(b =>
                      Number(b.id) === Number(cit.blockId) && Number(b.page) === cit.page
                    );
                    if (blk && blk.bbox) { precisionBbox = blk.bbox; break; }
                  }
                }
                // Synthetic page blocks (VLM/schematic docs) carry a
                // full-page bbox — drawing it would flood the whole page
                // with highlight. Treat ~page-sized bboxes as "no precise
                // geometry": jump to the page without a bbox overlay (the
                // text-layer fallback below still fires where a text layer
                // exists). 0.80, not 0.95: pdfplumber takes an E-plan's
                // drawing frame for a table and the row block covers 0.927
                // of the page (BA_Steinert_Steuerung_MMT S.86/87/91/92 — 8 of
                // 149 citations in the 2026-09-21 click test lit the whole
                // page). No real table row comes near 80 % of a page.
                const isFullPage = (b) => b &&
                  (Number(b.x1) - Number(b.x0)) * (Number(b.y1) - Number(b.y0)) >= 0.80;
                if (precisionBbox && isFullPage(precisionBbox)) precisionBbox = null;
                const q = (cit.quote || '').trim();
                const hasQuote = q.length >= 6;
                if (precisionBbox) {
                  viewerState.highlightBboxes = [precisionBbox];
                } else if (hasQuote) {
                  // No block geometry for this citation (blockId null or not
                  // on the cited page): the text layer anchors on the quote
                  // below. The old fallback painted EVERY bbox of the first
                  // chunk on the page — on a table page that was the whole
                  // table plus each residual cell block at once (2026-09-16
                  // REMA S.44 audit: 6 boxes per citation). Draw nothing.
                  viewerState.highlightBboxes = [];
                } else {
                  // No quote to anchor on either — keep the coarse page-region
                  // hint from the first matching chunk.
                  const first = chunksForPage[0];
                  const onPage = (first?.bboxes || []).filter(b =>
                    Number(b.page) === cit.page && !isFullPage(b));
                  viewerState.highlightBboxes = onPage;
                }
                // Text-prefix safety net: if the verifier-validated quote
                // is non-trivial, also pass it to PdfViewer.applyTextHighlight.
                // Bbox + text-layer highlights coexist on separate render
                // layers — when bbox is right, both overlap; when bbox is
                // wrong, the quote still anchors to its actual location.
                // Closes chrome-mcp 2026-05-21 audit's "no fallback" gap.
                // RAG_BLOCK_REF_CITATIONS: a citation with `ref` carries the
                // whole block text as quote (filled by the agent, up to 500
                // chars). PdfViewer highlights the full needle length from the
                // first prefix match, so a long table row smeared over the next
                // rows: with block geometry the bbox alone marks it; without,
                // only the first 120 chars anchor the text layer.
                if (hasQuote) {
                  if (!cit.ref) viewerState.highlightText = q;
                  else if (!precisionBbox) viewerState.highlightText = q.slice(0, 120);
                }
              }
            } else if (ext === 'xlsx') {
              // XLSX citations target a specific worksheet + row.
              // Prefer blockId as deterministic row-index when the
              // citation came from a query_xlsx synth chunk (each
              // such chunk emits one block with id=row_index so the
              // answer-gen LLM cites blockId=row_index). Fall back
              // to text-scoring only when blockId is missing.
              const sameDocChunks = (source.chunks || []);
              // Pick the specific chunk: first try blockId/rowIndex
              // match, then page match, then first xlsx chunk.
              let matchedChunk = null;
              if (cit.blockId != null) {
                matchedChunk = sameDocChunks.find(c =>
                  Number(c.rowIndex) === Number(cit.blockId)
                  || (c.blocks || []).some(b => Number(b.id) === Number(cit.blockId))
                );
              }
              if (!matchedChunk) {
                matchedChunk = sameDocChunks.find(c => Number(c.page) === cit.page);
              }
              const sheet = matchedChunk?.sheetName || matchedChunk?.sectionTitle || null;
              if (sheet) viewerState.initialSheet = sheet;
              // Row index wins over text scoring when present — the
              // xlsx has deterministic row positions, so a numeric
              // index is strictly better than a fuzzy match that
              // ties across duplicate rows.
              // Prefer cit.blockId: ingested chunks group many rows
              // and matchedChunk.blocks[0].id is just the chunk's
              // first row — collapsing every per-row citation onto
              // the same highlight. cit.blockId IS the cited row.
              const rowIdx = (cit.blockId != null)
                ? Number(cit.blockId)
                : (matchedChunk?.rowIndex ?? matchedChunk?.blocks?.[0]?.id);
              if (rowIdx != null) viewerState.rowIndex = Number(rowIdx);
              // Text signals retained as fallback for pre-reprocess
              // xlsx chunks that don't carry a rowIndex.
              const ctx = (localContext || '').trim();
              if (ctx.length >= 20) viewerState.highlightText = ctx;
              if (matchedChunk?.text) viewerState.scopeText = matchedChunk.text;
            }
            setDocumentViewerState(viewerState);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.target.click();
            }
          }}
        >
          [S.{cit.page}]
        </span>
      );
    };

    // Shared citation processing function
    const processTextWithCitations = (text) => {
      if (typeof text !== 'string') return text;

      // PRIMARY PATH: opaque markers ⟦N⟧ keyed to the citations array.
      // Always run when markers are present, even with an empty citations
      // array (streaming case): unresolved markers render as a greyed
      // placeholder pill that auto-upgrades to a clickable pill when the
      // matching citation_delta event arrives.
      const hasMarker = /⟦\d+⟧/.test(text);
      if (hasMarker) {
        const citById = new Map(
          (Array.isArray(citations) ? citations : []).map(c => [Number(c.id), c])
        );
        const parts = [];
        const markerRe = /⟦(\d+)⟧/g;
        let lastIdx = 0;
        let m;
        // Adjacent-duplicate suppression: two markers on the same line
        // separated only by whitespace whose citations share (docId,
        // page) would render identical [S.nn] pills back-to-back.
        let prevPill = null;
        while ((m = markerRe.exec(text)) !== null) {
          const between = text.substring(lastIdx, m.index);
          if (m.index > lastIdx) parts.push(between);
          if (between.trim() !== '') prevPill = null;
          const cit = citById.get(Number(m[1]));
          if (
            cit && prevPill &&
            prevPill.page === cit.page && prevPill.docId === cit.docId
          ) {
            lastIdx = markerRe.lastIndex;
            continue;
          }
          // Extract the LLM prose on the current bullet/line before
          // the marker — used as a per-citation locator for xlsx.
          // Strip the marker itself and trailing whitespace/paren.
          const preceding = text.substring(0, m.index);
          const lineBreak = Math.max(
            preceding.lastIndexOf('\n'),
            preceding.lastIndexOf('\r'),
          );
          const lineStart = lineBreak >= 0 ? lineBreak + 1 : 0;
          const localContext = preceding
            .substring(lineStart)
            .replace(/^[\s•\-\*\d.)\]]+/, '')
            .trim();
          if (cit) {
            parts.push(renderStructuredCitation(cit, `cit-${m.index}-${m[1]}`, localContext));
            prevPill = { docId: cit.docId, page: cit.page };
          } else {
            // Citation not yet streamed in — render a placeholder pill so
            // bullets retain their visual shape and the user sees that
            // this position is a (forthcoming) citation, not garbage.
            parts.push(
              <span
                key={`cit-pending-${m.index}-${m[1]}`}
                className="document-reference-link document-reference-pending"
                title="Quelle wird geladen…"
                aria-busy="true"
              >
                […]
              </span>
            );
            prevPill = null;
          }
          lastIdx = markerRe.lastIndex;
        }
        if (lastIdx < text.length) parts.push(text.substring(lastIdx));
        return parts.length > 0 ? parts : text;
      }

      // LEGACY FALLBACK: old [DocName, S.X, #N] / [S.X] markdown pattern.
      // Kept for backwards compatibility during the rollout; safe to remove once
      // every deployed backend emits structured citations.
      // Match patterns: [DocumentName, S.27, #3], [S.27, #3], [DocumentName, S.27], [S.27]
      // The optional #N block ID gives precise bbox highlighting when present.
      const citationPattern = /\[(?:([^\]]+?),\s*)?(?:S\.|p\.)(\d+)(?:,\s*#(\d+))?\]/g;
      const parts = [];
      let lastIndex = 0;
      let match;

      while ((match = citationPattern.exec(text)) !== null) {
        // Add text before the citation
        if (match.index > lastIndex) {
          parts.push(text.substring(lastIndex, match.index));
        }

        const docName = match[1] ? match[1].trim() : null;
        const pageNum = match[2];
        const blockId = match[3] != null ? parseInt(match[3], 10) : null;

        let source;
        if (docName) {
          // Full citation [DocName, S.X] — match against sources
          const normalizedDocName = normalize(docName);
          source = sources?.find(s => {
            const normalizedDisplayName = normalize(s.displayName);
            const normalizedDocId = normalize(s.docId);
            return (
              normalizedDocId === normalizedDocName ||
              normalizedDisplayName === normalizedDocName ||
              normalizedDisplayName.includes(normalizedDocName) ||
              normalizedDocName.includes(normalizedDisplayName)
            );
          });
        } else {
          // Shorthand [S.X] — find the source whose chunks actually contain this page.
          // sources[0] is unreliable: when the RAG returns multiple docs, the first
          // one may not be the one being discussed.
          const pageInt = parseInt(pageNum, 10);
          const matchingSources = (sources || []).filter(s =>
            (s.chunks || []).some(c => Number(c.page) === pageInt)
          );
          if (matchingSources.length === 1) {
            source = matchingSources[0];
          } else if (matchingSources.length > 1) {
            // Multiple sources have this page — pick the one whose displayName
            // appears most recently in the preceding text (best topical match).
            const precedingText = text.substring(0, match.index).toLowerCase();
            source = matchingSources.reduce((best, s) => {
              const idx = precedingText.lastIndexOf(normalize(s.displayName));
              const bestIdx = best ? precedingText.lastIndexOf(normalize(best.displayName)) : -1;
              return idx > bestIdx ? s : (best || s);
            }, null);
          } else {
            // No chunk has this page — fall back to first source
            source = sources?.[0];
          }
        }

        if (source && source.path) {
          const linkText = prettifyDocName(docName || source.displayName) || 'Dokument';
          const displayText = docName ? `${linkText}, S.${pageNum}` : `S.${pageNum}`;
          const ext = source.path.split('.').pop().toLowerCase();
          const inlineTypes = { pdf: 'pdf', docx: 'docx', xlsx: 'xlsx', csv: 'csv', xml: 'xml', stp: 'stp', dwg: 'dwg' };

          if (inlineTypes[ext]) {
            // Clickable citation — opens inline viewer
            const pageInt = parseInt(pageNum, 10);
            // Capture surrounding text for chunk selection when multiple chunks on same page
            const inlineContext = text.substring(Math.max(0, lastIndex), match.index).trim();
            parts.push(
              <span
                key={`citation-${match.index}`}
                className="document-reference-link"
                role="button"
                tabIndex={0}
                title={`${prettifyDocName(source.displayName) || linkText} öffnen`}
                onClick={(e) => {
                  capture("citation_clicked", {
                    docName: source.displayName || linkText,
                    page: pageInt,
                    ext,
                    kind: "legacy",
                  });
                  const viewerState = {
                    type: inlineTypes[ext],
                    url: source.path,
                    displayName: source.displayName || linkText,
                  };
                  if (ext === 'pdf') {
                    viewerState.page = pageInt;
                    // Find chunks that have content on this page.  A chunk
                    // may span multiple pages (chunk.page is only the start
                    // page), so check bboxes too.
                    const chunksForPage = (source.chunks || [])
                      .filter((c) =>
                        Number(c.page) === pageInt ||
                        (c.bboxes || []).some((b) => Number(b.page) === pageInt) ||
                        (c.blocks || []).some((b) => Number(b.page) === pageInt)
                      );
                    let bestChunk = chunksForPage[0];
                    let precisionBbox = null;
                    // Primary: #N block ID gives 100% precise single-bbox highlight.
                    // Require the block's page to match the cited page — LLMs
                    // sometimes emit #N referring to a block on a different
                    // page than the S.X they cited; don't trust a mismatched
                    // block ID, fall back to the page filter instead.
                    if (blockId != null) {
                      for (const chunk of chunksForPage) {
                        const blk = (chunk.blocks || []).find((b) => Number(b.id) === blockId);
                        if (blk && blk.bbox && Number(blk.page) === pageInt) {
                          precisionBbox = blk.bbox;
                          bestChunk = chunk;
                          break;
                        }
                      }
                    }
                    // Fallback: word-overlap scoring against chunk text (old chunks without blocks)
                    if (!precisionBbox && chunksForPage.length > 1) {
                      // Get citation context from DOM parent
                      let ctx = inlineContext;
                      if (ctx.length <= 2) {
                        const parent = e.target.closest('li') || e.target.closest('p');
                        if (parent) {
                          const allText = parent.textContent;
                          const myText = e.target.textContent;
                          const myIdx = allText.lastIndexOf(myText);
                          if (myIdx > 0) {
                            ctx = allText.substring(Math.max(0, myIdx - 300), myIdx)
                              .replace(/\[[^\]]*S\.\d+(?:,\s*#\d+)?\]/g, '').trim();
                          }
                        }
                      }
                      if (ctx.length > 2) {
                        const ctxWords = ctx.toLowerCase().split(/\s+/).filter(w => w.length >= 4);
                        let bestScore = -1;
                        for (const chunk of chunksForPage) {
                          const cl = (chunk.text || '').toLowerCase();
                          const score = ctxWords.reduce((s, w) => s + (cl.includes(w) ? 1 : 0), 0);
                          if (score > bestScore) { bestScore = score; bestChunk = chunk; }
                        }
                      }
                    }
                    if (precisionBbox) {
                      viewerState.highlightBboxes = [precisionBbox];
                    } else {
                      const chunkBboxes = bestChunk?.bboxes || [];
                      const onPage = chunkBboxes.filter((b) => Number(b.page) === pageInt);
                      viewerState.highlightBboxes = onPage.length > 0 ? onPage : chunkBboxes;
                    }
                  }
                  setDocumentViewerState(viewerState);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.target.click();
                  }
                }}
              >
                [{displayText}]
              </span>
            );
          } else {
            // Non-viewable source (DWG, STP, etc.) — plain citation
            parts.push(`[${displayText}]`);
          }
        } else {
          // No source found, keep original text
          const displayText = docName ? `${prettifyDocName(docName) || docName}, S.${pageNum}` : `S.${pageNum}`;
          parts.push(`[${displayText}]`);
        }

        lastIndex = citationPattern.lastIndex;
      }

      // Add remaining text
      if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
      }

      return parts.length > 0 ? parts : text;
    };

    // Shared function to process children recursively
    const processChildren = (children) => {
      return React.Children.map(children, child => {
        if (typeof child === 'string') {
          return processTextWithCitations(child);
        }
        // Recursively process nested elements (like <strong>, <em>, <li>, etc.)
        if (React.isValidElement(child) && child.props.children) {
          return React.cloneElement(child, {
            children: processChildren(child.props.children)
          });
        }
        return child;
      });
    };

    // Hoisted so the li renderer can identity-match paragraphs rendered
    // through this components map (their element type is this function,
    // not the host tag 'p').
    const ParagraphRenderer = ({ node, children, ...props }) => {
      return <p {...props}>{processChildren(children)}</p>;
    };

    return {
      // Custom text renderer to handle inline document citations
      p: ParagraphRenderer,
      // Process citations in list items. Items whose entire content is one
      // bold run ("Täglich:", "Wöchentlich:") are category subheads —
      // detected here rather than CSS :has(> strong:only-child), which
      // would false-positive on "**Achtung:** text…" (text nodes don't
      // count for only-child).
      li: ({ node, children, ...props }) => {
        // Loose lists wrap each item's content in a <p> — unwrap it first.
        const unwrap = (nodes) => {
          const arr = React.Children.toArray(nodes).filter(
            (k) => !(typeof k === 'string' && /^\s*$/.test(k))
          );
          if (
            arr.length === 1 &&
            React.isValidElement(arr[0]) &&
            (arr[0].type === 'p' || arr[0].type === ParagraphRenderer)
          ) {
            return unwrap(arr[0].props.children);
          }
          return arr;
        };
        const kids = unwrap(children);
        const isSubhead =
          kids.length > 0 &&
          kids.every((k) =>
            typeof k === 'string'
              ? /^[\s:]*$/.test(k)
              : React.isValidElement(k) && k.type === 'strong'
          ) &&
          kids.some((k) => React.isValidElement(k));
        return (
          <li className={isSubhead ? 'li-subhead' : undefined} {...props}>
            {processChildren(children)}
          </li>
        );
      },
      // Process citations in headings
      h1: ({ node, children, ...props }) => {
        return <h1 {...props}>{processChildren(children)}</h1>;
      },
      h2: ({ node, children, ...props }) => {
        return <h2 {...props}>{processChildren(children)}</h2>;
      },
      h3: ({ node, children, ...props }) => {
        return <h3 {...props}>{processChildren(children)}</h3>;
      },
      // Style links nicely
      a: ({ node, href, children, ...props }) => (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="markdown-link"
          {...props}
        >
          {children}
        </a>
      ),
      // Style code blocks
      code: ({ node, inline, children, ...props }) => {
        if (inline) {
          return <code className="inline-code" {...props}>{children}</code>;
        }
        return <code className="code-block" {...props}>{children}</code>;
      },
      // Wide GFM tables scroll horizontally instead of breaking the panel
      table: ({ node, children, ...props }) => (
        <div className="table-scroll">
          <table {...props}>{children}</table>
        </div>
      )
    };
  };

  // Identity-stable accessor for the markdown `components` object. Returns
  // the cached object when the inputs that affect rendering are unchanged
  // (so a re-render reconciles instead of remounting); recomputes only when
  // the source list, the cited content, or the section doc actually change
  // (e.g. while a citation streams in). `citations` is rebuilt fresh on
  // every render (scopedCitations is a .filter() result), so it's compared
  // by content signature, not reference.
  const getMarkdownComponents = (cacheKey, sources, citations, sectionDocId = null) => {
    const cache = mdComponentsCacheRef.current;
    const citSig = JSON.stringify(citations || []);
    const hit = cache.get(cacheKey);
    if (hit && hit.sources === sources && hit.sectionDocId === sectionDocId && hit.citSig === citSig) {
      return hit.components;
    }
    const components = createMarkdownComponents(sources, citations, sectionDocId);
    cache.set(cacheKey, { sources, sectionDocId, citSig, components });
    return components;
  };

  const renderMessage = (message, index, isLoading) => {
    const isBot = message.from === "bot";
    const isLastMessage = index === messages.length - 1;
    const previousMessage = messages[index - 1];
    const showTimestamp = !previousMessage || previousMessage.from !== message.from ||
      (new Date(message.timestamp) - new Date(previousMessage.timestamp)) > 300000; // 5 minutes

    return (
      <div key={message.id} className={`message ${isBot ? "bot-message" : "user-message"}`}>
        <div className="message-content">
          {showTimestamp && (
            <div className="message-time">
              {new Date(message.timestamp).toLocaleTimeString('de-DE', {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          )}

          {message.isWelcome && (
            <div className="welcome-card">
              <div className="welcome-title">Willkommen! 👋</div>
              <div className="welcome-description">
                Ihr KI-Assistent für Dokumentation. Wählen Sie unten eine der empfohlenen Aktionen aus oder stellen Sie Ihre eigene Frage.
              </div>
            </div>
          )}

          {/* Thinking trace — the agent's work, live and afterwards.
              Steps arrive as `thinking` events (wording + phase owned by
              rag_agent/react/labels.py); their outcome arrives as the
              matching `observation` and is attached to the step that
              produced it. A running step shows ⏳, a finished one its
              duration. */}
          {isBot && message.progressSteps && message.progressSteps.length > 0 && (() => {
            const expanded = isThinkingExpanded(message);
            const steps = message.progressSteps;
            const running = message.status === "thinking" || message.status === "generating_answer";
            const current = steps[steps.length - 1];
            // Progress counter: only the deterministic plan executor knows
            // the total up front. The ReAct loop decides its next tool
            // after each observation, so it reports a count, not a ratio.
            const counter = current && current.step
              ? `Schritt ${current.step}${current.of ? ` von ${current.of}` : ""}`
              : `${steps.length} ${steps.length === 1 ? "Schritt" : "Schritte"}`;
            const firstAt = steps[0] && steps[0].at;
            const lastAt = steps[steps.length - 1] && (steps[steps.length - 1].doneAt || steps[steps.length - 1].at);
            const total = (firstAt && lastAt && lastAt > firstAt) ? formatDuration(lastAt - firstAt) : "";
            // While the agent works the header IS the live step. Afterwards it
            // is a toggle, so it names the action — "anzeigen" on an already
            // open panel contradicts both the arrow and what a click does.
            const header = running
              ? `${(current && current.text) || "Analysiere Frage …"} · ${counter}`
              : `Denkprozess ${expanded ? "ausblenden" : "anzeigen"} (${steps.length} ${steps.length === 1 ? "Schritt" : "Schritte"}${total ? ` · ${total}` : ""})`;
            return (
              <div className="thinking-trace-container">
                <button
                  className={`thinking-trace-toggle${running ? " is-running" : ""}`}
                  onClick={() => toggleThinkingTrace(message)}
                  aria-expanded={expanded}
                  title={header}
                >
                  <span className="toggle-icon">{expanded ? "▼" : "▶"}</span>
                  <span className="toggle-text">{header}</span>
                </button>

                {expanded && (
                  <div className="thinking-trace-panel">
                    {steps.map((step, i) => {
                      const isLast = i === steps.length - 1;
                      const active = running && isLast && !step.doneAt;
                      const took = step.doneAt && step.at ? formatDuration(step.doneAt - step.at) : "";
                      return (
                        <div key={i} className={`trace-step phase-${step.phase || "processing"}${active ? " is-active" : ""}`}>
                          <span className="step-icon">{phaseIcon(step.phase)}</span>
                          <span className="step-body">
                            <span className="step-text">{step.text}</span>
                            {step.result && <span className="step-result">{step.result}</span>}
                          </span>
                          {took && <span className="step-duration">{took}</span>}
                          <span className={`step-status${active ? " active" : ""}`}>{active ? "⏳" : "✓"}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Message bubble — section-list render, uniform path.
              `message.answerSections` is built progressively during the
              stream (one entry per section_open event, body grows via
              section_delta, citation_ids set on section_close). Each
              section renders independently with its scoped citations,
              so the layout is identical during streaming and after the
              final `message` event — no end-of-stream layout flip.

              Falls back to flat `message.text` rendering for non-
              section answers (welcome, clarifications, errors, legacy
              backends). */}
          {(() => {
          // Short answer first (RAG_SHORT_ANSWER): the full answer below folds
          // under "Details" once the answer is final and long enough.
          const showShort = shouldShowShort(message);
          const withShort = (inner) => !showShort ? inner : (
            <>
              <div className="short-answer">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={getMarkdownComponents(`${message.id}-short`, message.sources, message.citations)}
                >
                  {message.shortAnswer}
                </ReactMarkdown>
              </div>
              <details className="answer-fold answer-details">
                <summary>{detailsLabel(fullAnswerText(message), { streaming: message.status !== "sent" })}</summary>
                {inner}
              </details>
            </>
          );
          return (Array.isArray(message.answerSections) && message.answerSections.length > 0) ? (
            <div className="message-bubble">
              {withShort(message.answerSections.map((section, idx) => {
                const sourceEntry = (message.sources || []).find(
                  s => s && s.docId === section.docId
                );
                // Distinguish "no heading was set" (explicit empty string
                // from the deterministic doc-list renderer) from "heading
                // was set but resolved to nothing" (legacy fallback).
                // Prefer the deterministic displayName lookup; never fall
                // back to the answer-LLM's section.heading (it can be
                // hallucinated / stale). When neither source resolves,
                // show a deterministic docId snippet — accurate, opaque,
                // unmistakably non-prose.
                const explicitlyNoHeading = section.heading === '';
                const heading = explicitlyNoHeading
                  ? ''
                  : (
                      prettifyDocName(sourceEntry && sourceEntry.displayName)
                      || (section.docId ? `Quelle ${section.docId.slice(0, 8)}` : 'Quelle')
                    );
                const sectionCitationIds = new Set(
                  Array.isArray(section.citation_ids) ? section.citation_ids : []
                );
                // During streaming the citations array is global; scope
                // it down to this section's ids when set, else show all
                // (best-effort while citation_ids hasn't streamed in
                // yet — section_close populates it).
                const scopedCitations = sectionCitationIds.size > 0
                  ? (message.citations || []).filter(c => sectionCitationIds.has(c.id))
                  : (message.citations || []);
                const body = typeof section.content === 'string' ? section.content : '';
                return (
                  <div key={`${message.id}-sec-${idx}`} className="answer-section">
                    {heading && <h3 className="answer-section-heading">{heading}</h3>}
                    <FoldedMarkdown
                      final={message.status === "sent"}
                      question={previousMessage && previousMessage.from === "user" ? previousMessage.text : ""}
                      md={normalizeMarkdownLists(body)}
                      components={getMarkdownComponents(`${message.id}-sec-${idx}`, message.sources, scopedCitations, section.docId)}
                    />
                  </div>
                );
              }))}
            </div>
          ) : message.text ? (
            <div className="message-bubble">
              {withShort(
              <FoldedMarkdown
                final={message.status === "sent"}
                question={previousMessage && previousMessage.from === "user" ? previousMessage.text : ""}
                md={normalizeMarkdownLists(message.text)}
                components={getMarkdownComponents(`${message.id}-text`, message.sources, message.citations)}
              />
              )}
            </div>
          ) : null;
          })()}

          {/* Clarification options - clickable buttons for user to respond */}
          {message.isClarification && message.clarificationOptions && message.clarificationOptions.length > 0 && (
            <div className="clarification-options">
              {message.clarificationOptions.map((option, i) => {
                // Support both string and object options
                // Object format: {display: "text user sees", value: "text||docId"}
                const displayText = typeof option === 'object' ? option.display : option;
                const valueText = typeof option === 'object' ? option.value : option;
                return (
                  <button
                    type="button"
                    key={i}
                    className="clarification-option"
                    onClick={() => handleSuggestionClick(valueText)}
                    disabled={isLoading}
                  >
                    {displayText}
                  </button>
                );
              })}
            </div>
          )}

          {message.suggestions && message.suggestions.length > 0 && (
            <div className="suggestion-chips">
              {message.suggestions.map((suggestion, i) => {
                const label = typeof suggestion === "string" ? suggestion : suggestion.label;
                return (
                  <button
                    type="button"
                    key={i}
                    className="suggestion-chip"
                    onClick={() => handleSuggestionClick(suggestion)}
                    disabled={isLoading}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}

          {Array.isArray(message.citations) && message.citations.length > 0 && typeof message.citations[0] === 'string' && (
            <div className="citations-panel">
              <div className="citations-header">
                <div className="citations-icon">📚</div>
                <div className="citations-count">
                  {message.citations.length} {message.citations.length === 1 ? 'Dokument' : 'Dokumente'} verwendet
                </div>
              </div>
              <div className="citations-list">
                {message.citations.slice(0, 3).map((citation, i) => (
                  <div key={i} className="citation-item">{citation}</div>
                ))}
                {message.citations.length > 3 && (
                  <div className="citation-more">+{message.citations.length - 3} weitere</div>
                )}
              </div>
            </div>
          )}
          
          <div className="message-actions">
            <button
              type="button"
              className={`message-action-btn ${copiedMessageId === message.id ? 'copied' : ''}`}
              onClick={() => copyMessage(message.text, message.id)}
              title="In Zwischenablage kopieren"
            >
              {copiedMessageId === message.id ? "✅ Kopiert!" : "📋 Kopieren"}
            </button>
            {/* A canned chip reply has no question behind it: regenerating
                would send the bare chip label to the agent. */}
            {isBot && isLastMessage && !message.isCanned && (
              <button
                type="button"
                className="message-action-btn"
                onClick={() => regenerateResponse(messages[index - 1]?.text)}
                title="Antwort neu generieren"
              >
                🔄 Neu generieren
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  /* ---------- render ---------- */
  return (
    <div
      className={`chat-container ${isChatOpen ? "chat-open" : ""}`}
      style={{
        width: `${chatWidth}px`,
        right: isChatOpen ? 0 : `-${chatWidth}px`
      }}
    >
      {/* --- resize handle --- */}
      {isChatOpen && (
        <div
          className="resize-handle"
          onMouseDown={startResize}
          title="Drag to resize"
        />
      )}

      {/* --- side tab --- */}
      <button
        type="button"
        className="chat-toggle"
        onClick={toggleChat}
        aria-pressed={isChatOpen}
        aria-label={isChatOpen ? "Chat schließen" : "Chat öffnen"}
      >
        <span className="chat-toggle-pill" aria-hidden="true">
          <span className="chat-pill-accent" />
          <span className="chat-pill-label">AI</span>
        </span>
        <span
          className={`chat-toggle-arrow ${isChatOpen ? "expanded" : ""}`}
          aria-hidden="true"
        >
          {isChatOpen ? "›" : "‹"}
        </span>
      </button>

      {/* --- panel --- */}
      {isChatOpen && (
        <div className="chat-content-wrapper">
          {/* chat header */}
          <div className="chat-header">
            <div className="chat-header-title">
              <span className="status-dot"></span>
              KI Assistent
              {isTyping && <span className="typing-status">tippt...</span>}
            </div>
            <div className="chat-header-actions">
              <button
                type="button"
                className="chat-action-btn"
                onClick={clearChat}
                title="Chat löschen"
                aria-label="Chat löschen"
              >
                <TrashIcon className="chat-action-icon" />
              </button>
              <button
                type="button"
                className="chat-action-btn"
                onClick={exportChat}
                title="Chat exportieren"
                aria-label="Chat exportieren"
              >
                <DownloadIcon className="chat-action-icon" />
              </button>
              <button
                type="button"
                className="chat-close-btn"
                onClick={toggleChat}
                title="Chat schließen"
                aria-label="Chat schließen"
              >
                <CloseIcon className="chat-action-icon" />
              </button>
            </div>
          </div>

          {/* messages area (overlay lives here so header + input stay visible) */}
          <div className="chat-messages-area">
            {/* overlay (only over the message list, not the whole panel) */}
            {showOverlay && (
              <div className="overlay">
                <div className="overlay-card">
                  <p>
                    Die Online-Doku gilt nur als Unterstützung und bringt keinerlei Verbindlichkeiten oder Haftungen mit sich. Die vorliegende Dokumentation in Papierform ist die Dokumentation gemäß Maschinenrichtlinie und hat stets Vorrang.
                  </p>
                  <button type="button" className="overlay-btn" onClick={acceptOverlay}>
                    Akzeptieren
                  </button>
                </div>
              </div>
            )}

            {/* offline: the assistant needs the backend — keep the panel visible, block input */}
            {!showOverlay && !isOnline && (
              <div className="overlay">
                <div className="overlay-card">
                  <p>Der KI-Chat ist nur online verfügbar. Gespeicherte Dokumente und die Suche funktionieren weiterhin.</p>
                </div>
              </div>
            )}

            {/* scrollable messages */}
            <div className="chat-content" ref={chatContentRef}>
              {messages.map((message, index) => renderMessage(message, index, isLoading))}
              {/* Bouncing dots only until the bot bubble exists. `isLoading`
                  stays true for the whole request (it is cleared in the
                  finally block), so keying the indicator on it alone parked
                  a second, empty bot bubble underneath the answer for the
                  entire streaming phase. Once the placeholder message is in
                  the list, the thinking trace and the streaming text ARE the
                  progress indicator. */}
              {isLoading && (messages.length === 0 || messages[messages.length - 1].from === "user") && (
                <div className="message bot-message">
                  <div className="message-content">
                    <div className="typing-indicator">
                      <div className="typing-dot"></div>
                      <div className="typing-dot"></div>
                      <div className="typing-dot"></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* floating jump-to-bottom / follow button (zero-height anchor
              keeps it glued above the input row without affecting layout) */}
          {isAwayFromBottom && !stickyMode && (
            <div className="chat-jump-bottom-anchor">
              <button
                type="button"
                className="chat-jump-bottom-btn"
                onClick={() => {
                  setStickyMode(true);
                  scrollToBottom();
                }}
                title="Nach unten folgen"
                aria-label="Nach unten folgen"
              >
                <ArrowDownIcon className="chat-action-icon" />
              </button>
            </div>
          )}

          {/* input */}
          <div className="chat-input">
            <div className="input-group">
              <textarea
                ref={inputRef}
                className="chat-input-field"
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  setIsTyping(true);
                  setTimeout(() => setIsTyping(false), 1000);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Schreiben Sie hier Ihre Nachricht..."
                disabled={isLoading || showOverlay || !isOnline}
                rows={1}
              />
              <button
                type="button"
                className="send-button"
                onClick={sendMessage}
                disabled={isLoading || !input.trim() || showOverlay || !isOnline}
              >
                Senden
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Memoized on purpose. Chat's only props are stable useState setters
// (setDocumentViewerState, onChatToggle), so it must NOT re-render just
// because the parent App re-renders for an unrelated reason. Clicking a
// citation flips App's `documentViewerState` (to open the inline document
// viewer); without memo that cascaded a Chat re-render, which rebuilt the
// react-markdown `components` object with fresh function identities and
// thereby remounted the whole message subtree inside the scroll container —
// resetting the chat scroll to the top of the message. memo severs that
// cascade so opening a citation never touches the chat scroll position.
export default React.memo(Chat);
